import { isParentUser } from 'src/utils/userRole';
import CustomAvatar from 'src/designSystem/Avatar';
import { User } from 'src/types/user.types';
import { useTranslation } from 'react-i18next';
import Avatar from 'src/components/Avatar/Avatar';
import {
  useContext,
  useEffect,
  useRef,
  useState,
  FunctionComponent,
} from 'react';
import { twMerge } from 'src/utils/twMerge';
import PanelHeader from 'src/designSystem/PanelHeader';
import Spinner from 'src/designSystem/Spinner';
import Pill from 'src/designSystem/Pill';
import { CloseIcon } from 'src/designSystem/icons';
import { UserContext } from 'src/contexts/UserContext';
import { AutoFloatingPanel } from '../designSystem/AutoFloatingPanel';
import { reportError } from 'src/modules/logs/Sentry';
import type { FamilyMemberResponse } from 'src/types/user.types';
import { getPressableProps } from 'src/utils/pressable';
import { logAnalyticsEvent } from 'src/modules/analytics/analytics';

const TILE_STAGGER_MS = 45;

export type ProfilePickerSource =
  | 'app_launch'
  | 'profile_unknown_on_device'
  | 'profile_menu'
  | 'parent_lesson'
  | 'family_code';

type Props = {
  isOpen: boolean;
  onClose?: () => void;
  users?: (User | FamilyMemberResponse)[];
  onSelectUser: (user: User | FamilyMemberResponse) => Promise<unknown>;
  onAddNewUser?: () => Promise<unknown> | void;
  title?: string;
  isClosable?: boolean;
  isEntranceAnimated?: boolean;
  onPanelHeightChange?: (height: number) => void;
  analyticsSource: ProfilePickerSource;
  greetingIndex?: number;
};

const UserSelect: FunctionComponent<Props> = ({
  isOpen,
  onClose,
  users,
  onSelectUser,
  onAddNewUser,
  title,
  isClosable = true,
  isEntranceAnimated = false,
  onPanelHeightChange,
  analyticsSource,
  greetingIndex,
}) => {
  const [pendingTile, setPendingTile] = useState<number | 'add' | null>(null);
  const { t } = useTranslation();
  const me = useContext(UserContext)?.me;
  const profilesCount = users?.length ?? 0;
  const pickerProperties = {
    profile_picker_source: analyticsSource,
    profiles_count: profilesCount,
    greeting_index: greetingIndex ?? null,
  };

  const isViewLogged = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      isViewLogged.current = false;
      return;
    }
    if (!profilesCount || isViewLogged.current) return;
    isViewLogged.current = true;
    logAnalyticsEvent('profile_picker_viewed', {
      profile_picker_source: analyticsSource,
      profiles_count: profilesCount,
      greeting_index: greetingIndex ?? null,
    });
  }, [isOpen, profilesCount, analyticsSource, greetingIndex]);

  const runTile = async (
    tile: number | 'add',
    action: () => Promise<unknown> | void,
  ) => {
    if (pendingTile !== null) return;
    setPendingTile(tile);
    try {
      await action();
      if (onClose) {
        onClose();
        setPendingTile(null);
      }
    } catch (error) {
      logAnalyticsEvent('profile_switch_failed', pickerProperties);
      reportError('Failed to switch user:', error);
      setPendingTile(null);
    }
  };

  if (!users?.length) {
    return null;
  }

  const tileClassName = (tile: number | 'add') =>
    twMerge(
      'flex flex-col items-center gap-2 min-w-0 transition-opacity duration-md',
      pendingTile !== null && pendingTile !== tile && 'opacity-50',
      isEntranceAnimated &&
        'animate-profile-tile-in motion-reduce:animate-none',
    );
  const tileStyle = (index: number) =>
    isEntranceAnimated
      ? { animationDelay: `${220 + index * TILE_STAGGER_MS}ms` }
      : undefined;

  return (
    <AutoFloatingPanel
      isOpen={isOpen}
      onClose={onClose}
      closable={isClosable}
      isBackdropHidden={!isClosable}
      onPanelHeightChange={onPanelHeightChange}
      className={
        isClosable ? undefined : '[&_.adm-floating-panel-bar]:invisible'
      }
      data-testid="user-select"
    >
      {!!title && <PanelHeader title={title} />}
      <div
        role="group"
        className="grid grid-cols-3 gap-x-3 gap-y-5"
        aria-busy={pendingTile !== null}
      >
        {users.map((user, index) => {
          const familyMemberType =
            user.family_member_type ??
            (me && user.id === me.id ? me.family_member_type : undefined);
          const avatar = (user as User).avatar;

          return (
            <div
              {...getPressableProps()}
              key={user.id}
              className={tileClassName(user.id)}
              style={tileStyle(index)}
              data-testid={`user-switch-${user.firstname}-${'lastname' in user ? (user.lastname ?? '') : ''}`}
              onClick={() =>
                runTile(user.id, () => {
                  logAnalyticsEvent('profile_picker_profile_selected', {
                    ...pickerProperties,
                    selected_user_type: isParentUser(user) ? 'parent' : 'child',
                    is_current_profile: user.id === me?.id,
                  });
                  return onSelectUser(user);
                })
              }
            >
              <div className="relative size-[68px] rounded-full overflow-hidden bg-structural-lighter flex items-center justify-center">
                {avatar ? (
                  <Avatar
                    avatar={avatar}
                    familyMemberType={familyMemberType}
                    className="w-full h-full object-cover object-top scale-150 translate-y-1/4"
                    isSkeletonShown
                  />
                ) : (
                  <CustomAvatar
                    name={user.firstname ?? undefined}
                    // eslint-disable-next-line baobab/no-off-weight-title -- an avatar initial, not a title: Figma sets it in Gilroy-SemiBold
                    className="h-full w-full text-2xl text-white font-semibold"
                  />
                )}
                {pendingTile === user.id && (
                  <div className="absolute inset-0 flex items-center justify-center bg-structural-darkest/50">
                    <Spinner
                      size="small"
                      type="white"
                      data-testid="profile-switch-spinner"
                    />
                  </div>
                )}
              </div>
              <div className="text-sm font-semibold leading-tight text-structural-darkest max-w-[100px] truncate">
                {user.firstname}
              </div>
              {isParentUser(user) && (
                <Pill className="rounded-lg px-3 py-2 bg-primary-light text-primary-darkest font-semibold">
                  {t('profile.switchUser.parent')}
                </Pill>
              )}
            </div>
          );
        })}
        {!!onAddNewUser && (
          <div
            {...getPressableProps()}
            className={tileClassName('add')}
            style={tileStyle(users.length)}
            data-testid="btn-add-profile"
            onClick={() =>
              runTile('add', () => {
                logAnalyticsEvent(
                  'profile_picker_add_clicked',
                  pickerProperties,
                );
                return onAddNewUser();
              })
            }
          >
            <div className="relative size-[68px] rounded-full flex items-center justify-center bg-structural-lighter">
              {pendingTile === 'add' ? (
                <Spinner size="small" data-testid="profile-switch-spinner" />
              ) : (
                <CloseIcon className="size-5 rotate-45" />
              )}
            </div>
            <div className="text-sm font-semibold leading-tight text-structural-darkest text-center">
              {t('profile.swichUser.addNewUser')}
            </div>
          </div>
        )}
      </div>
    </AutoFloatingPanel>
  );
};

export default UserSelect;
